import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Card, CardBody, Badge, Input, Label, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import classnames from "classnames";
import TiqueteCorrespondenciaPDF from '../../../components/TiqueteCorrespondenciaPDF';
import GestionesCaso from '../GestionesCaso';
import SignatureModal from '../../../components/SignatureModal';
import { FaEye, FaDownload, FaUser, FaMapMarkerAlt, FaPhone, FaFileAlt, FaInfoCircle, FaCalendarAlt, FaHashtag, FaPenNib } from 'react-icons/fa';
import { IoPlaySharp, IoPauseSharp, IoStopSharp, IoCheckmarkDoneSharp } from "react-icons/io5";
import { getCasosRead, patchUpdateActa, postCasosStatusChange, postFinalizarCaso, postImagenesInforme, patchCasosUpdate, getFirmasBase64 } from '../../../helpers/backend';
import SweetAlert from "react-bootstrap-sweetalert";
import Swal from "sweetalert2";
import { generateMapImage } from '../../../utils/mapImageGenerator';


// Convierte una ruta de firma (relativa o absoluta) a URL absoluta
const toAbsoluteUrl = (src) => {
  if (!src || typeof src !== 'string') return src;
  if (src.startsWith('http') || src.startsWith('data:') || src.startsWith('blob:')) return src;
  const baseUrl = process.env.REACT_APP_API_URL?.split('/api')[0] || '';
  return `${baseUrl}${src.startsWith('/') ? '' : '/'}${src}`;
};

// Embebe una firma como base64 para que react-pdf no dependa de descargar la URL remota
// (evita que el PDF quede sin firma si la imagen recién subida no carga a tiempo).
// Si algo falla, devuelve la fuente original (comportamiento previo).
const firmaToBase64 = async (src) => {
  try {
    if (!src || typeof src !== 'string') return src;
    if (src.startsWith('data:')) return src;
    // El media requiere autenticación: enviar el token (igual que la descarga de archivos).
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const resp = await fetch(toAbsoluteUrl(src), { headers });
    if (!resp.ok) return src;
    const blob = await resp.blob();
    // Solo embeber si realmente es una imagen (evita firmas corruptas si la URL
    // devolviera HTML/JSON con 200). Si no, se deja la URL original (sin regresión).
    if (!blob || !blob.type || !blob.type.startsWith('image/')) return src;
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(src);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return src;
  }
};


const CorrespondenciaModal = ({ isOpen, toggle, caso, onUpdate }) => {
  const [casoData, setCasoData] = useState(null);
  const [loading, setLoading] = useState(false);

  const dataURLtoFile = (dataurl, filename) => {
    if (!dataurl || typeof dataurl !== 'string' || !dataurl.includes(',')) return null;
    try {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    } catch (e) {
      console.error("Error converting dataURL to File:", e);
      return null;
    }
  };

  // Comprime imágenes antes de almacenarlas para evitar problemas de memoria en móvil
  const compressImage = (file, maxDimension = 900, quality = 0.72) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          if (width >= height) {
            height = Math.round(height * maxDimension / width);
            width = maxDimension;
          } else {
            width = Math.round(width * maxDimension / height);
            height = maxDimension;
          }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => {
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
        }, 'image/jpeg', quality);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  };

  // Estados para acciones
  const [actionAlert, setActionAlert] = useState(false);
  const [action, setAction] = useState("");
  const [ticket, setTicket] = useState("");
  const [ticketId, setTicketId] = useState(null);
  const [observaciones, setObservaciones] = useState("");
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);
  const [coordenadas, setCoordenadas] = useState(""); // Agregado coordenadas

  // Estados para firma
  const [showRepresentanteSignature, setShowRepresentanteSignature] = useState(false);
  const [showCiudadLimpiaSignature, setShowCiudadLimpiaSignature] = useState(false);
  const [tempRepresentanteFirma, setTempRepresentanteFirma] = useState(null);
  const [tempCiudadLimpiaFirma, setTempCiudadLimpiaFirma] = useState(null);
  const [fileRepresentanteFirma, setFileRepresentanteFirma] = useState(null);
  const [fileCiudadLimpiaFirma, setFileCiudadLimpiaFirma] = useState(null);

  // Estados para datos editables del reclamante
  const [nombreReclamante, setNombreReclamante] = useState("");
  const [cedulaReclamante, setCedulaReclamante] = useState("");
  const [telefonoReclamante, setTelefonoReclamante] = useState("");
  const [fechaEntrega, setFechaEntrega] = useState(null);

  // Estados para Causas de Devolución
  const [coVacio, setCoVacio] = useState(false);
  const [coNoHayQuienReciba, setCoNoHayQuienReciba] = useState(false);
  const [coRehusado, setCoRehusado] = useState(false);
  const [coDireccionErrada, setCoDireccionErrada] = useState(false);
  const [coAltoRiesgo, setCoAltoRiesgo] = useState(false);
  const [coErrorEnLosDatos, setCoErrorEnLosDatos] = useState(false);
  const [coCorreoCertificado, setCoCorreoCertificado] = useState(false);
  const [conSello, setConSello] = useState(false);
  // Sello: una sola imagen del sello físico (se muestra en pág. 1 junto al mapa)
  const [archivoSello, setArchivoSello] = useState(null);
  const [previewSello, setPreviewSello] = useState(null);
  // Fotos: evidencias fotográficas múltiples (se muestran en pág. 3)
  const [archivosFotos, setArchivosFotos] = useState([]);
  const [previewsFotos, setPreviewsFotos] = useState([]);
  // CNP: una sola imagen para la página 2
  const [archivoCNP, setArchivoCNP] = useState(null);
  const [previewCNP, setPreviewCNP] = useState(null);
  const fileInputSelloRef = useRef(null);
  const fileInputFotosRef = useRef(null);
  const fileInputCNPRef = useRef(null);
  // Bloquea el cierre del modal mientras el selector de archivos/cámara está abierto
  const isPickingFileRef = useRef(false);

  // Estados para vista previa del ticket
  const [isTicketPreviewOpen, setIsTicketPreviewOpen] = useState(false);
  const [ticketPreviewData, setTicketPreviewData] = useState(null);
  const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);

  // Estado para Tabs
  const [activeTab, setActiveTab] = useState("1");

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Cierre seguro: no cierra si hay selector de archivos abierto
  const safeToggle = () => {
    if (isPickingFileRef.current) return;
    toggle();
  };

  // En móvil, cuando la cámara devuelve el foco a la app, limpiamos el bloqueo
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(() => { isPickingFileRef.current = false; }, 300);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Función para limpiar TODOS los estados del modal
  const resetAllStates = () => {
    setCasoData(null);
    setNombreReclamante("");
    setCedulaReclamante("");
    setTelefonoReclamante("");
    setFechaEntrega(null);
    setTempRepresentanteFirma(null);
    setTempCiudadLimpiaFirma(null);
    setFileRepresentanteFirma(null);
    setFileCiudadLimpiaFirma(null);
    setCoordenadas("");
    setObservaciones("");
    setCoVacio(false);
    setCoNoHayQuienReciba(false);
    setCoRehusado(false);
    setCoDireccionErrada(false);
    setCoAltoRiesgo(false);
    setCoErrorEnLosDatos(false);
    setCoCorreoCertificado(false);
    setConSello(false);
    setArchivoSello(null);
    setPreviewSello(null);
    setArchivoCNP(null);
    setPreviewCNP(null);
    setArchivosFotos([]);
    setPreviewsFotos([]);
    setTicketPreviewData(null);
    setIsTicketPreviewOpen(false);
    setActionAlert(false);
    setAction("");
    setTicket("");
    setTicketId(null);
    setActiveTab("1");
  };

  useEffect(() => {
    if (isOpen && caso?.id) {
      // Primero resetear TODO para evitar datos del caso anterior
      resetAllStates();

      // Luego cargar datos frescos del caso actual
      fetchCasoData();

      // Extraer datos del informe (prioridad) y metadata como fallback
      const meta = caso.metadata?.correspondencia || {};
      const informe = caso.informe || {};

      // Datos del representante/destinatario desde informe (prioridad) o metadata (fallback)
      const meta_telef = Array.isArray(meta.telefonos_reclamante)
        ? meta.telefonos_reclamante.join(' / ')
        : (meta.telefonos_reclamante || '');

      setNombreReclamante(informe.representante_nombre || meta.nombre_reclamante || '');
      setCedulaReclamante(informe.representante_cedula || meta.cedula_reclamante || '');
      setTelefonoReclamante(informe.representante_telefono_celular || meta_telef || '');

      // Cargar firmas desde informe (siempre explícito para evitar datos del caso anterior)
      setTempRepresentanteFirma(informe.representante_firma || null);
      setFileRepresentanteFirma(null);
      setTempCiudadLimpiaFirma(informe.ciudad_limpia_firma || null);
      setFileCiudadLimpiaFirma(null);

      // Fecha de entrega desde informe
      setFechaEntrega(informe.fecha_informe ? new Date(informe.fecha_informe) : null);

      // Cargar causas de devolución desde informe
      setCoVacio(!!informe.co_vacio);
      setCoNoHayQuienReciba(!!informe.co_no_hay_quien_reciba);
      setCoRehusado(!!informe.co_rehusado);
      setCoDireccionErrada(!!informe.co_direccion_errada);
      setCoAltoRiesgo(!!informe.co_alto_riesgo);
      setCoErrorEnLosDatos(!!informe.co_error_en_los_datos);
      setCoCorreoCertificado(!!informe.co_correo_certificado);
      setConSello(!!informe.con_sello);

      // Cargar sello previo (label 'Sello Recibido')
      const baseUrl = process.env.REACT_APP_API_URL?.split('/api')[0] || '';
      const selloPrevio = (informe.images || caso.images || []).find(img => img.label === 'Sello Recibido');
      if (selloPrevio) {
        const imgUrl = selloPrevio.image || selloPrevio.image_url;
        setPreviewSello(imgUrl.startsWith('http') ? imgUrl : `${baseUrl}${imgUrl}`);
      }
      // Cargar CNP previo (label 'CNP')
      const cnpPrevio = (informe.images || caso.images || []).find(img => img.label === 'CNP');
      if (cnpPrevio) {
        const imgUrl = cnpPrevio.image || cnpPrevio.image_url;
        setPreviewCNP(imgUrl.startsWith('http') ? imgUrl : `${baseUrl}${imgUrl}`);
      }
      // Cargar fotos previas (label 'Evidencia Extra')
      const fotosPrevias = (informe.images || caso.images || []).filter(img => img.label === 'Evidencia Extra');
      if (fotosPrevias.length > 0) {
        const prevs = fotosPrevias.map(img => {
          const imgUrl = img.image || img.image_url;
          return imgUrl.startsWith('http') ? imgUrl : `${baseUrl}${imgUrl}`;
        });
        setPreviewsFotos(prevs);
      }
    } else if (!isOpen) {
      // Al cerrar el modal, limpiar TODO para no contaminar el próximo caso
      resetAllStates();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, caso?.id]);

  const fetchCasoData = async () => {
    try {
      setLoading(true);
      const response = await getCasosRead(caso.id);
      console.log('Datos del caso recibidos:', response.data);

      setCasoData(response.data);

      // Actualizar los estados con los datos más recientes del informe
      const informe = response.data.informe || {};

      // Actualizar datos del representante desde informe
      setNombreReclamante(informe.representante_nombre || '');
      setCedulaReclamante(informe.representante_cedula || '');
      setTelefonoReclamante(informe.representante_telefono_celular || '');

      // Actualizar firmas desde informe (siempre explícito para limpiar datos del caso anterior)
      setTempRepresentanteFirma(informe.representante_firma || null);
      setFileRepresentanteFirma(null);
      setTempCiudadLimpiaFirma(informe.ciudad_limpia_firma || null);
      setFileCiudadLimpiaFirma(null);

      // Actualizar fecha de entrega desde informe
      setFechaEntrega(informe.fecha_informe ? new Date(informe.fecha_informe) : null);

      // Actualizar causas de devolución desde informe
      setCoVacio(!!informe.co_vacio);
      setCoNoHayQuienReciba(!!informe.co_no_hay_quien_reciba);
      setCoRehusado(!!informe.co_rehusado);
      setCoDireccionErrada(!!informe.co_direccion_errada);
      setCoAltoRiesgo(!!informe.co_alto_riesgo);
      setCoErrorEnLosDatos(!!informe.co_error_en_los_datos);
      setCoCorreoCertificado(!!informe.co_correo_certificado);
      setConSello(!!informe.con_sello);

      // Sincronizar imágenes con el backend (siempre, para reflejar el estado real)
      const baseUrl2 = process.env.REACT_APP_API_URL?.split('/api')[0] || '';
      const todasImagenes = informe.images || response.data.images || [];

      const selloPrevio2 = todasImagenes.find(img => img.label === 'Sello Recibido');
      const imgUrlSello = selloPrevio2 ? (selloPrevio2.image || selloPrevio2.image_url) : null;
      setPreviewSello(imgUrlSello ? (imgUrlSello.startsWith('http') ? imgUrlSello : `${baseUrl2}${imgUrlSello}`) : null);

      const cnpPrevio2 = todasImagenes.find(img => img.label === 'CNP');
      const imgUrlCNP = cnpPrevio2 ? (cnpPrevio2.image || cnpPrevio2.image_url) : null;
      setPreviewCNP(imgUrlCNP ? (imgUrlCNP.startsWith('http') ? imgUrlCNP : `${baseUrl2}${imgUrlCNP}`) : null);

      const fotosPrevias2 = todasImagenes.filter(img => img.label === 'Evidencia Extra');
      setPreviewsFotos(fotosPrevias2.map(img => {
        const imgUrl = img.image || img.image_url;
        return imgUrl.startsWith('http') ? imgUrl : `${baseUrl2}${imgUrl}`;
      }));
      // Los archivos locales ya fueron subidos: limpiarlos para no subirlos de nuevo
      setArchivosFotos([]);
      setArchivoSello(null);
      setArchivoCNP(null);

    } catch (error) {
      console.error("Error fetching caso data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Determinar si el caso está en estado editable (EN_PROCESO)
  const estadoActual = (casoData || caso)?.estado;
  const isEditable = estadoActual === 'EN_PROCESO';
  const isFinalizado = estadoActual === 'FINALIZADO';

  // Bloquear edición si la OT no está iniciada
  const handleBlockedAction = () => {
    Swal.fire({
      icon: 'warning',
      title: 'OT no iniciada',
      text: 'Debe iniciar la OT antes de poder realizar cambios. Por favor, inicie la orden de trabajo primero.',
      confirmButtonColor: '#28a745',
      confirmButtonText: 'Entendido'
    });
  };

  const handleSaveRepresentanteSignature = (file) => {
    if (!isEditable) { handleBlockedAction(); return; }
    setFileRepresentanteFirma(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const base64data = reader.result;
      setTempRepresentanteFirma(base64data);
      setFechaEntrega(new Date()); // Guardar fecha de entrega al firmar
    };
  };

  const handleSaveCiudadLimpiaSignature = (file) => {
    setFileCiudadLimpiaFirma(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const base64data = reader.result;
      setTempCiudadLimpiaFirma(base64data);
    };
  };
  const handleCaptureSello = async (e) => {
    isPickingFileRef.current = false;
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    Swal.fire({ title: 'Procesando imagen...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const compressed = await compressImage(file);
      setArchivoSello(compressed);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewSello(reader.result);
        Swal.close();
        Swal.fire({ icon: 'success', title: 'Sello capturado', timer: 1200, showConfirmButton: false });
      };
      reader.readAsDataURL(compressed);
    } catch (err) {
      Swal.close();
      console.error('Error al procesar imagen del sello:', err);
    }
  };

  const handleCapturaFoto = async (e) => {
    isPickingFileRef.current = false;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = null;
    Swal.fire({ title: 'Procesando imagen(es)...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const comprimidas = await Promise.all(files.map(f => compressImage(f)));
      comprimidas.forEach(file => {
        setArchivosFotos(prev => [...prev, file]);
        const reader = new FileReader();
        reader.onloadend = () => setPreviewsFotos(prev => [...prev, reader.result]);
        reader.readAsDataURL(file);
      });
      Swal.close();
      Swal.fire({ icon: 'success', title: `${files.length} foto(s) agregada(s)`, timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.close();
      console.error('Error al procesar fotos:', err);
    }
  };

  const triggerCameraSello = () => {
    isPickingFileRef.current = true;
    if (fileInputSelloRef.current) fileInputSelloRef.current.click();
  };

  const triggerCameraFotos = () => {
    isPickingFileRef.current = true;
    if (fileInputFotosRef.current) fileInputFotosRef.current.click();
  };

  const handleCaptureCNP = async (e) => {
    isPickingFileRef.current = false;
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    Swal.fire({ title: 'Procesando imagen...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const compressed = await compressImage(file);
      setArchivoCNP(compressed);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewCNP(reader.result);
        Swal.close();
        Swal.fire({ icon: 'success', title: 'CNP capturado', timer: 1200, showConfirmButton: false });
      };
      reader.readAsDataURL(compressed);
    } catch (err) {
      Swal.close();
      console.error('Error al procesar imagen CNP:', err);
    }
  };

  const triggerCameraCNP = () => {
    isPickingFileRef.current = true;
    if (fileInputCNPRef.current) fileInputCNPRef.current.click();
  };

  const handleUpdateCorrespondencia = async () => {
    if (!isEditable) { handleBlockedAction(); return; }
    if (!conSello) {
      if (nombreReclamante && /\d/.test(nombreReclamante)) {
        Swal.fire({ icon: 'warning', title: 'Nombre inválido', text: 'El nombre no debe contener números.', confirmButtonColor: '#28a745' });
        return;
      }
      if (cedulaReclamante) {
        const esNumero = /^\d+$/.test(cedulaReclamante.trim());
        const esNIT = /^\d{1,15}-\d$/.test(cedulaReclamante.trim());
        if (!esNumero && !esNIT) {
          Swal.fire({ icon: 'warning', title: 'Formato inválido', text: 'La Cédula debe contener solo números, o el NIT debe tener el formato: 1234567890-5', confirmButtonColor: '#28a745' });
          return;
        }
      }
    }
    try {
      setLoading(true);
      const formData = new FormData();

      // Enviar los datos del representante como campos individuales del FormData
      // (igual que en ActaVisita.js - NO como objeto JSON)
      formData.append('representante_nombre', nombreReclamante || '');
      formData.append('representante_cedula', cedulaReclamante || '');
      formData.append('representante_telefono_celular', telefonoReclamante || '');

      // Agregar causas de devolución como parte del informe (serializado o campos individuales si el backend lo permite)
      // Como patchUpdateActa maneja formdata plano, enviamos claves directas que el backend mergeará en informe si está configurado así,
      // O si el backend espera un objeto JSON 'informe', debemos construirlo.
      // Basado en handleFinalizar, parece que se prefiere 'informe' como JSON string, pero handleUpdateCorrespondencia usa campos planos.
      // Vamos a enviar campos planos para que el backend los capture si tiene los campos en el modelo, OTAMBIEN los agregamos
      // reconstruyendo el objeto informe si es necesario.
      // Para seguridad, enviamos los campos planos con prefijo co_ que parece ser lo que el usuario indica.

      formData.append('co_vacio', coVacio);
      formData.append('co_no_hay_quien_reciba', coNoHayQuienReciba);
      formData.append('co_rehusado', coRehusado);
      formData.append('co_direccion_errada', coDireccionErrada);
      formData.append('co_alto_riesgo', coAltoRiesgo);
      formData.append('co_error_en_los_datos', coErrorEnLosDatos);
      formData.append('co_correo_certificado', coCorreoCertificado);
      formData.append('con_sello', conSello);

      if (fechaEntrega) {
        // Formato YYYY-MM-DD requerido por el backend
        const fechaStr = fechaEntrega instanceof Date
          ? fechaEntrega.toISOString().split('T')[0]
          : new Date(fechaEntrega).toISOString().split('T')[0];
        formData.append('fecha_informe', fechaStr);
      }

      // Archivos de firma (solo si se han capturado nuevos)
      if (fileRepresentanteFirma instanceof File) {
        formData.append('representante_firma', fileRepresentanteFirma);
      }
      if (fileCiudadLimpiaFirma instanceof File) {
        formData.append('ciudad_limpia_firma', fileCiudadLimpiaFirma);
      }

      // Debug: mostrar contenido del FormData
      console.log('=== ENVIANDO ACTUALIZACIÓN ===');
      console.log('Caso ID:', caso.id);
      console.log('Datos enviados en FormData:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value);
      }

      const response = await patchUpdateActa(caso.id, formData);

      // Subir sello si hay uno nuevo
      if (archivoSello instanceof File && casoData?.informe?.id) {
        try {
          const imgFormData = new FormData();
          imgFormData.append('informe_id', casoData.informe.id);
          imgFormData.append('image', archivoSello);
          imgFormData.append('label', 'Sello Recibido');
          await postImagenesInforme(imgFormData);
          console.log('✅ Sello guardado');
        } catch (imgErr) {
          console.error('❌ Error al guardar el sello:', imgErr);
        }
      }
      // Subir CNP si hay uno nuevo
      if (archivoCNP instanceof File && casoData?.informe?.id) {
        try {
          const imgFormData = new FormData();
          imgFormData.append('informe_id', casoData.informe.id);
          imgFormData.append('image', archivoCNP);
          imgFormData.append('label', 'CNP');
          await postImagenesInforme(imgFormData);
          console.log('✅ CNP guardado');
        } catch (imgErr) {
          console.error('❌ Error al guardar el CNP:', imgErr);
        }
      }
      // Subir fotos de evidencia
      if (archivosFotos.length > 0 && casoData?.informe?.id) {
        try {
          for (const file of archivosFotos) {
            const imgFormData = new FormData();
            imgFormData.append('informe_id', casoData.informe.id);
            imgFormData.append('image', file);
            imgFormData.append('label', 'Evidencia Extra');
            await postImagenesInforme(imgFormData);
          }
          console.log('✅ Fotos de evidencia guardadas');
        } catch (imgErr) {
          console.error('❌ Error al guardar las fotos:', imgErr);
        }
      }

      console.log('=== RESPONSE RECIBIDO ===');
      console.log('Response completo:', response);
      console.log('Response data:', response?.data);
      console.log('Response status:', response?.status);

      // Refrescar datos del backend para sincronizar imágenes y limpiar archivos locales ya subidos
      await fetchCasoData();

      Swal.fire({
        icon: 'success',
        title: 'Actualizado',
        text: 'La información se ha actualizado correctamente.',
        timer: 2000,
        showConfirmButton: false
      });

      // Al depender de caso?.id (no de caso), onUpdate no dispara un reset del modal
      if (onUpdate) onUpdate();

    } catch (error) {
      console.error("=== ERROR EN ACTUALIZACIÓN ===");
      console.error("Error completo:", error);
      console.error("Error response:", error.response);
      console.error("Error data:", error.response?.data);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar la información.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (actionType, ticketName, id) => {
    // 1. Loading Inmediato
    Swal.fire({
      title: 'Validando...',
      text: 'Verificando permisos y ubicación...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // 2. Validación Geolocalización
    if (!navigator.geolocation) {
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'La geolocalización no está soportada por este navegador.'
      });
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
      const { latitude, longitude } = position.coords;
      setCoordenadas(latitude + " " + longitude);

      // Cerrar loading y abrir confirmación
      Swal.close();

      setAction(actionType);
      setTicket(ticketName);
      setTicketId(id);
      setActionAlert(true); // Abre el SweetAlert de confirmación

    } catch (error) {
      console.error("Error obteniendo ubicación:", error);
      Swal.close();
      Swal.fire({
        icon: 'warning',
        title: 'Ubicación Requerida',
        text: 'Para continuar, es OBLIGATORIO autorizar el acceso a su ubicación. Por favor, habilite los permisos y vuelva a intentarlo.',
        confirmButtonColor: "#28a745",
      });
    }
  };

  const actionSuccess = async () => {
    try {
      // Mostrar spinner de carga
      Swal.fire({
        title: 'Cargando...',
        text: 'Procesando solicitud...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Replicando estructura de payload de CasosDetails.js
      // CORRECCIÓN: El backend espera 'accion' en lugar de 'estado'
      const payload = {
        accion: action, // Enviamos la acción (INICIAR, PAUSAR, etc.) con la clave 'accion'
        observaciones: observaciones,
        coordenadas: coordenadas
      };

      await postCasosStatusChange(ticketId, payload);
      setActionAlert(false); // Cierra el modal de confirmación si estaba abierto

      // Cerrar loading y mostrar éxito
      Swal.close();
      setShowAlertSuccess(true);

      fetchCasoData(); // Recargar datos
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error updating status:", error);
      Swal.close();
      setErrorAlert(true);
    }
  };

  const handleFinalizar = async () => {
    // 1. VALIDACIONES DE FORMULARIO (antes de geolocalización)
    if (!conSello) {
      if (nombreReclamante && /\d/.test(nombreReclamante)) {
        Swal.fire({ icon: 'warning', title: 'Nombre inválido', text: 'El nombre no debe contener números.', confirmButtonColor: '#28a745' });
        return;
      }
      if (cedulaReclamante) {
        const esNumero = /^\d+$/.test(cedulaReclamante.trim());
        const esNIT = /^\d{1,15}-\d$/.test(cedulaReclamante.trim());
        if (!esNumero && !esNIT) {
          Swal.fire({ icon: 'warning', title: 'Formato inválido', text: 'La Cédula debe contener solo números, o el NIT debe tener el formato: 1234567890-5', confirmButtonColor: '#28a745' });
          return;
        }
      }
    }

    // Evidencias fotográficas son obligatorias
    if (previewsFotos.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Evidencias requeridas', text: 'Debe subir al menos una foto de evidencia para poder finalizar el caso.', confirmButtonColor: '#28a745', confirmButtonText: 'Entendido' });
      return;
    }

    // 2. VALIDACIÓN DE UBICACIÓN
    let currentLatitude = null;
    let currentLongitude = null;
    // Coordenadas EFECTIVAS para el mapa/registro: en un ajuste de acta ya finalizada
    // serán las de la ubicación ORIGINAL; en una finalización normal serán las actuales.
    let mapLatitude = null;
    let mapLongitude = null;
    let mapImageBase64 = null;

    if (!navigator.geolocation) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'La geolocalización no está soportada por este navegador.'
      });
      return;
    }

    try {
      // Mostrar Loading para Mapa
      Swal.fire({
        title: 'Obteniendo ubicación y mapa...',
        text: 'Por favor espere mientras generamos la evidencia de ubicación.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      currentLatitude = position.coords.latitude;
      currentLongitude = position.coords.longitude;
      setCoordenadas(currentLatitude + " " + currentLongitude);

      // Por defecto, usar la ubicación actual (finalización normal)
      mapLatitude = currentLatitude;
      mapLongitude = currentLongitude;
      // Si el acta YA fue finalizada antes (es un AJUSTE), usar la ubicación ORIGINAL
      // donde se hizo la orden (primera gestión con coordenadas), no el GPS de quien ajusta.
      try {
        const gestionesPrev = (casoData?.gestiones) || [];
        // La ubicación correcta es DONDE SE ENTREGÓ = la PRIMERA gestión FINALIZADO con
        // coordenadas. No se usa la de INICIADO porque el agente pudo iniciar en la empresa
        // y finalizar en el sitio de entrega.
        const primeraFinalizacion = gestionesPrev.find(
          g => g.estado === 'FINALIZADO' && g.coordenadas && String(g.coordenadas).trim()
        );
        if (primeraFinalizacion) {
          const partes = String(primeraFinalizacion.coordenadas).trim().split(/[\s,]+/).map(parseFloat).filter(n => !isNaN(n));
          if (partes.length >= 2) {
            mapLatitude = partes[0];
            mapLongitude = partes[1];
            console.log('📍 Ajuste de acta: usando ubicación de la PRIMERA finalización', mapLatitude, mapLongitude);
          }
        }
      } catch (e) {
        // Si algo falla, se mantiene la ubicación actual
      }

      // 2. GENERAR IMAGEN DEL MAPA (FRONTEND)
      // Generamos el mapa ANTES de preguntar, para tenerlo listo para el PDF
      try {
        console.log('🗺️ Generando mapa para:', mapLatitude, mapLongitude);
        mapImageBase64 = await generateMapImage(mapLatitude, mapLongitude);
        if (mapImageBase64) {
          console.log('✅ Mapa generado exitosamente (Base64)');
        }
      } catch (mapError) {
        console.error('⚠️ Error generando mapa:', mapError);
        // No detenemos el proceso, pero el PDF irá sin mapa o con fallback
      }

      Swal.close();

    } catch (error) {
      console.error("Error obteniendo ubicación:", error);
      Swal.close();
      Swal.fire({
        icon: 'warning',
        title: 'Ubicación Requerida',
        text: 'Para finalizar el caso, es OBLIGATORIO autorizar el acceso a su ubicación. Por favor, habilite los permisos y vuelva a intentarlo.',
        confirmButtonColor: "#28a745",
      });
      return;
    }

    // 3. CONFIRMACIÓN
    const result = await Swal.fire({
      title: '¿Finalizar Caso?',
      text: "¿Está seguro que desea finalizar este caso de correspondencia? Se adjuntará su ubicación actual y el mapa generado.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, finalizar'
    });

    if (result.isConfirmed) {
      try {
        // Mostrar Loading Finalización
        Swal.fire({
          title: 'Finalizando Caso',
          text: 'Guardando datos, generando PDF y finalizando...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const now = new Date();
        const fechaActual = now.toISOString().split('T')[0];
        const horaActual = now.toTimeString().slice(0, 5);

        // 4. ACTUALIZAR DATOS DEL INFORME
        console.log('🚀 Guardando datos bases del informe...');
        const updateFormData = new FormData();
        updateFormData.append('representante_nombre', nombreReclamante || '');
        updateFormData.append('representante_cedula', cedulaReclamante || '');
        updateFormData.append('representante_telefono_celular', telefonoReclamante || '');
        updateFormData.append('fecha_visita', fechaActual);
        updateFormData.append('hora_visita', horaActual);
        updateFormData.append('fecha_informe', fechaEntrega ? fechaEntrega.toISOString().split('T')[0] : fechaActual);

        // Causas de devolución
        updateFormData.append('co_vacio', coVacio);
        updateFormData.append('co_no_hay_quien_reciba', coNoHayQuienReciba);
        updateFormData.append('co_rehusado', coRehusado);
        updateFormData.append('co_direccion_errada', coDireccionErrada);
        updateFormData.append('co_alto_riesgo', coAltoRiesgo);
        updateFormData.append('co_error_en_los_datos', coErrorEnLosDatos);
        updateFormData.append('co_correo_certificado', coCorreoCertificado);
        updateFormData.append('con_sello', conSello);

        // Coordenadas (efectivas: original si es ajuste, actual si es finalización normal)
        updateFormData.append('latitude', mapLatitude);
        updateFormData.append('longitude', mapLongitude);

        // Guardar imagen base64 de mapa si el backend tiene un campo para ello (opcional, pero útil)
        if (mapImageBase64) {
          // Si el backend acepta base64 directo:
          updateFormData.append('location_map_image', mapImageBase64);
        }

        // Convertir firmas base64 a archivos
        const fileFirmaRep = dataURLtoFile(tempRepresentanteFirma, 'firma_reclamante.png');
        if (fileFirmaRep) updateFormData.append('representante_firma', fileFirmaRep);
        else if (fileRepresentanteFirma instanceof File) updateFormData.append('representante_firma', fileRepresentanteFirma);

        const fileFirmaFunc = dataURLtoFile(tempCiudadLimpiaFirma, 'firma_funcionario.png');
        if (fileFirmaFunc) updateFormData.append('ciudad_limpia_firma', fileFirmaFunc);
        else if (fileCiudadLimpiaFirma instanceof File) updateFormData.append('ciudad_limpia_firma', fileCiudadLimpiaFirma);

        await patchUpdateActa(casoData.id, updateFormData);
        console.log('✅ Informe actualizado');

        // Subir sello (una imagen)
        if (archivoSello instanceof File && casoData?.informe?.id) {
          try {
            const imgFormData = new FormData();
            imgFormData.append('informe_id', casoData.informe.id);
            imgFormData.append('image', archivoSello);
            imgFormData.append('label', 'Sello Recibido');
            await postImagenesInforme(imgFormData);
            console.log('✅ Sello guardado en la galería');
          } catch (e) {
            console.error('Error guardando sello:', e);
          }
        }
        // Subir CNP
        if (archivoCNP instanceof File && casoData?.informe?.id) {
          try {
            const imgFormData = new FormData();
            imgFormData.append('informe_id', casoData.informe.id);
            imgFormData.append('image', archivoCNP);
            imgFormData.append('label', 'CNP');
            await postImagenesInforme(imgFormData);
            console.log('✅ CNP guardado en la galería');
          } catch (e) {
            console.error('Error guardando CNP:', e);
          }
        }
        // Subir fotos de evidencia
        if (archivosFotos.length > 0 && casoData?.informe?.id) {
          try {
            for (const file of archivosFotos) {
              const imgFormData = new FormData();
              imgFormData.append('informe_id', casoData.informe.id);
              imgFormData.append('image', file);
              imgFormData.append('label', 'Evidencia Extra');
              await postImagenesInforme(imgFormData);
            }
            console.log('✅ Fotos de evidencia guardadas en la galería');
          } catch (e) {
            console.error('Error guardando fotos de evidencia:', e);
          }
        }

        // 5. SUBIR IMAGEN DEL MAPA A LA GALERÍA (Si es posible)
        // Esto asegura que también esté en las evidencias visuales del backend
        if (mapImageBase64 && casoData?.informe?.id) {
          try {
            console.log('🚀 Subiendo mapa a galería de evidencias...');
            const mapFile = dataURLtoFile(mapImageBase64, 'mapa_ubicacion.png');
            if (mapFile) {
              const imgFormData = new FormData();
              imgFormData.append('informe_id', casoData.informe.id);
              // Nombre del campo depende del backend, usualmente 'image' o 'images'
              imgFormData.append('image', mapFile);
              imgFormData.append('label', 'Ubicación de Finalización');
              await postImagenesInforme(imgFormData);
              console.log('✅ Mapa subido a galería');
            }
          } catch (imgError) {
            console.warn('⚠️ No se pudo subir el mapa a la galería (no crítico):', imgError);
          }
        }

        // 6. GENERAR PDF FINAL (Con el mapa generado en frontend)
        console.log('=== GENERANDO PDF FINAL ===');

        // Refresh casoData para obtener las URLs reales de las fotos subidas, y no forzar base64 gigante en react-pdf
        let updatedCasoData = casoData;
        try {
          const freshDataResponse = await getCasosRead(casoData.id);
          if (freshDataResponse.data) {
            updatedCasoData = freshDataResponse.data;
          }
        } catch (e) {
          console.log("Aviso: No se pudo refrescar data de caso antes de PDF", e);
        }

        // Inyectamos activamente la fecha y hora que acabamos de usar para que el PDF no salga vacío
        const pdfData = mapDataToPDF(mapImageBase64, updatedCasoData, {
          fecha_informe: fechaEntrega ? fechaEntrega.toISOString().split('T')[0] : fechaActual,
          hora_visita: horaActual,
          locationCoords: `${mapLatitude}, ${mapLongitude}`
        });

        // Aseguramos que el PDF use este mapa
        if (mapImageBase64) {
          pdfData.locationImage = mapImageBase64;
        }

        // Embeber las firmas como base64 para que SIEMPRE queden en el PDF.
        // Preferir el base64 que entrega el backend (lee el archivo directo del disco,
        // sin CORS ni problemas de carga). Si no, se intenta descargar desde el front.
        let repFirmaSrc = pdfData.representante_firma;
        let funcFirmaSrc = pdfData.ciudad_limpia_firma;
        try {
          const firmasResp = await getFirmasBase64(casoData.id);
          if (firmasResp?.data?.representante_firma_base64) repFirmaSrc = firmasResp.data.representante_firma_base64;
          if (firmasResp?.data?.ciudad_limpia_firma_base64) funcFirmaSrc = firmasResp.data.ciudad_limpia_firma_base64;
        } catch (e) {
          // Si falla, se usa lo que ya traía pdfData
        }
        pdfData.representante_firma = await firmaToBase64(repFirmaSrc);
        pdfData.ciudad_limpia_firma = await firmaToBase64(funcFirmaSrc);

        const pdfDocument = <TiqueteCorrespondenciaPDF data={pdfData} />;
        const pdfBlob = await pdf(pdfDocument).toBlob();
        const fileName = `Ticket_Correspondencia_${casoData?.name || 'documento'}_${fechaActual}.pdf`;
        const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

        // 7. FINALIZAR CASO CON PDF
        const finalizeFormData = new FormData();
        finalizeFormData.append('phone', '');
        finalizeFormData.append('coordenadas', `${mapLatitude} ${mapLongitude}`);
        finalizeFormData.append('pdf_file', pdfFile);

        // También enviamos al campo de evidencias si se requiere
        finalizeFormData.append('informe_pdf_evidencias', pdfFile);

        // Informe JSON
        finalizeFormData.append('informe', JSON.stringify({
          fecha_visita: fechaActual,
          hora_visita: horaActual,
          locationCoords: `${mapLatitude}, ${mapLongitude}`,
          // Incluir base64 en json por si acaso
          location_map_image: mapImageBase64
        }));

        await postFinalizarCaso(casoData.id, finalizeFormData);
        console.log('✅ Caso finalizado exitosamente');

        // 8. ASEGURAR PDF EN CAMPO DE EVIDENCIAS (Doble check)
        // A veces postFinalizarCaso no guarda en ambos campos, hacemos update extra por seguridad
        try {
          const extraPdfData = new FormData();
          extraPdfData.append('informe_pdf_evidencias', pdfFile);
          await patchUpdateActa(casoData.id, extraPdfData);
        } catch (e) {
          console.log('Info: No se requirió update extra de PDF o falló silenciosamente.');
        }

        Swal.close();
        Swal.fire({
          icon: 'success',
          title: '¡Éxito!',
          text: 'El caso ha sido finalizado correctamente con todas las evidencias.',
          timer: 3000
        });

        await fetchCasoData();
        if (onUpdate) onUpdate();

      } catch (error) {
        console.error("Error en proceso de finalización:", error);
        Swal.close();

        let errorMsg = error.message || 'Error desconocido';
        if (error.response && error.response.data && error.response.data.error) {
          errorMsg = error.response.data.error;
        }

        Swal.fire('Error', 'Hubo un problema al finalizar: ' + errorMsg, 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const mapDataToPDF = (localLocationImage = null, explicitCasoData = null, overrides = {}) => {
    // Usar explicitCasoData si se provee (para tener los datos más recientes post-polling)
    const sourceData = explicitCasoData || casoData;

    if (!sourceData) return {};
    const meta = sourceData.metadata?.correspondencia || {};
    const informe = sourceData.informe || {};

    // Obtener información de visitas si existe
    const visitas = sourceData.visitas || sourceData.metadata?.visitas || [];

    // Procesar visitas para el formato del PDF
    const visitasProcesadas = visitas.map(visita => ({
      funcionario: visita.funcionario_nombre || visita.nombre_funcionario || visita.funcionario || '',
      codigo: visita.funcionario_codigo || visita.codigo || '',
      fecha: visita.fecha || '',
      hora: visita.hora || '',
      observaciones: visita.observaciones || ''
    }));

    // Formatear teléfonos del reclamante si viene como array
    let telefonoReclamanteMeta = meta.telefonos_reclamante;
    if (Array.isArray(telefonoReclamanteMeta)) {
      telefonoReclamanteMeta = telefonoReclamanteMeta.join(' / ');
    }

    // Procesar firmas: Si son File objects, crear URL temporal
    let repFirma = tempRepresentanteFirma || informe.representante_firma;
    let funcFirma = tempCiudadLimpiaFirma || informe.ciudad_limpia_firma;

    if (repFirma instanceof File) {
      repFirma = URL.createObjectURL(repFirma);
    }
    if (funcFirma instanceof File) {
      funcFirma = URL.createObjectURL(funcFirma);
    }

    return {
      // IMPORTANTE: Incluimos el objeto 'informe' completo para que el PDF pueda acceder a informe.images
      informe: informe,

      guia: sourceData.name || '',
      planilla: meta.planilla || '',
      no_resolucion: meta.no_resolucion || '',

      remitente: 'CIUDAD LIMPIA',

      // Datos del DESTINATARIO (Reclamante) extraídos del metadata
      destinatarioNombre: meta.nombre_reclamante || '',
      destinatarioDireccion: meta.direccion_reclamante || '',
      destinatarioTelefono: telefonoReclamanteMeta || '',
      destinatarioCedula: meta.cedula_reclamante || '',

      // Fechas de correspondencia
      fecha_original: meta.fecha_original,
      fecha_limite: meta.fecha_limite,

      // Datos de ENTREGA - Priorizar el estado local para la vista previa
      recibidoNombre: nombreReclamante || informe.representante_nombre || '',
      recibidoCedula: cedulaReclamante || informe.representante_cedula || '',
      recibidoTelefono: telefonoReclamante || informe.representante_telefono_celular || telefonoReclamanteMeta || '',
      fechaEntrega: fechaEntrega || new Date(),

      // Fecha y hora exactas del informe para el PDF
      fecha_informe: informe.fecha_informe || null,
      hora_visita: informe.hora_visita || null,

      observaciones: [sourceData.descripcion, sourceData.observaciones, meta.observaciones]
        .filter(val => val && val !== 'null' && val !== 'undefined')
        .join(' ') || '',
      visitas: visitasProcesadas,

      // Firmas procesadas
      representante_firma: repFirma,
      ciudad_limpia_firma: funcFirma,

      // Datos del funcionario
      ciudad_limpia_nombre: informe.ciudad_limpia_nombre,
      ciudad_limpia_codigo: informe.ciudad_limpia_codigo,

      // Datos de ubicación persistidos
      locationImage: localLocationImage || informe.location_map_image ||
        (informe.images?.find(img => img.label === 'Ubicación de Finalización')?.image) ||
        (informe.images?.find(img => img.label === 'Ubicación de Finalización')?.image_url) ||
        (informe.images?.find(img => String(img.image || img.image_url).includes('ubicacion'))?.image) ||
        (informe.images?.find(img => String(img.image || img.image_url).includes('ubicacion'))?.image_url) ||
        null,
      locationCoords: informe.latitude && informe.longitude ? `${informe.latitude}, ${informe.longitude}` : (sourceData.coordenadas || null),

      // Agregar también los datos del funcionario a las visitas para mostrar en el PDF
      funcionario_nombre: informe.ciudad_limpia_nombre,
      funcionario_codigo: informe.ciudad_limpia_codigo,
      // Causas de devolución (se pueden marcar desde el formulario)
      causas: {
        vacío: coVacio,
        noHayQuienReciba: coNoHayQuienReciba,
        rehusado: coRehusado,
        direccionErrada: coDireccionErrada,
        altoRiesgo: coAltoRiesgo,
        errorDatos: coErrorEnLosDatos,
        correoCertificado: coCorreoCertificado
      },
      conSello: conSello,
      // Imagen del sello (para pág. 1 junto al mapa)
      selloImage: (() => {
        const remoteSello = (informe.images || sourceData.images || []).find(img => img.label === 'Sello Recibido');
        const imgUrl = remoteSello ? (remoteSello.image || remoteSello.image_url) : previewSello;
        if (!imgUrl) return null;
        if (imgUrl.startsWith('data:') || imgUrl.startsWith('http')) return imgUrl;
        const baseUrl = process.env.REACT_APP_API_URL?.split('/api')[0] || '';
        return `${baseUrl}${imgUrl}`;
      })(),
      // Imagen del CNP (para pág. 2)
      cnpImage: (() => {
        const remoteCNP = (informe.images || sourceData.images || []).find(img => img.label === 'CNP');
        const imgUrl = remoteCNP ? (remoteCNP.image || remoteCNP.image_url) : previewCNP;
        if (!imgUrl) return null;
        if (imgUrl.startsWith('data:') || imgUrl.startsWith('http')) return imgUrl;
        const baseUrl = process.env.REACT_APP_API_URL?.split('/api')[0] || '';
        return `${baseUrl}${imgUrl}`;
      })(),
      // Fotos de evidencia (para pág. 3)
      fotosImages: (() => {
        const remoteFotos = (informe.images || sourceData.images || []).filter(img => img.label === 'Evidencia Extra');
        const useRemote = remoteFotos.length > 0 && remoteFotos.length >= archivosFotos.length;
        const imagesToUse = useRemote ? remoteFotos.map(img => img.image || img.image_url) : previewsFotos;
        return imagesToUse.map(imgUrl => {
          if (!imgUrl) return null;
          if (imgUrl.startsWith('data:') || imgUrl.startsWith('http')) return imgUrl;
          const baseUrl = process.env.REACT_APP_API_URL?.split('/api')[0] || '';
          return `${baseUrl}${imgUrl}`;
        }).filter(url => url);
      })(),
      ...overrides
    };
  };

  const handleViewTicket = async () => {
    setIsGeneratingTicket(true);
    try {
      console.log('Generando vista previa del ticket...');
      const data = mapDataToPDF();
      console.log('Datos mapeados para PDF:', data);

      // Validar datos mínimos
      if (!data.guia) {
        console.warn('No se encontró número de guía');
      }
      if (!data.destinatario) {
        console.warn('No se encontró destinatario');
      }

      // Probar a generar el componente PDF para ver si hay errores
      try {
        const pdfComponent = <TiqueteCorrespondenciaPDF data={data} />;
        console.log('Componente PDF creado exitosamente');

        // Intentar generar el blob para ver si hay errores
        const testBlob = await pdf(pdfComponent).toBlob();
        console.log('Blob de prueba generado:', testBlob.size, 'bytes');

      } catch (pdfError) {
        console.error('Error en generación de PDF:', pdfError);
        throw new Error('Error al generar el PDF: ' + pdfError.message);
      }

      setTicketPreviewData(data);
      console.log('Datos del ticket establecidos, abriendo modal...');
      setIsTicketPreviewOpen(true);
    } catch (error) {
      console.error('Error preparando vista previa del ticket:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo generar la vista previa del ticket: ' + error.message,
        timer: 3000
      });
    } finally {
      setIsGeneratingTicket(false);
    }
  };

  const handleDownloadTicket = async () => {
    try {
      console.log('Descargando ticket...');
      const data = mapDataToPDF();
      console.log('Datos para descarga:', data);

      // Validar que el componente PDF pueda ser creado
      if (!data.guia) {
        console.warn('Advertencia: No se encontró número de guía para el PDF');
      }

      const pdfDocument = <TiqueteCorrespondenciaPDF data={data} />;
      console.log('Documento PDF creado, generando blob...');

      const blob = await pdf(pdfDocument).toBlob();
      console.log('Blob generado:', blob.size, 'bytes');

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Ticket_${casoData?.name || 'Correspondencia'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar URL después de un tiempo
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      console.log('Descarga completada');
    } catch (error) {
      console.error('Error descargando el ticket:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al descargar',
        text: 'No se pudo descargar el PDF: ' + error.message,
        timer: 5000
      });
    }
  };

  // Función para ver el PDF guardado en el backend (cuando el caso está finalizado)
  const handleViewSavedPDF = () => {
    if (casoData?.informe_pdf) {
      // Construir la URL completa del PDF
      const baseUrl = process.env.REACT_APP_API_URL?.split('/api')[0] || '';
      const pdfUrlBase = casoData.informe_pdf.startsWith('http')
        ? casoData.informe_pdf
        : `${baseUrl}${casoData.informe_pdf}`;
      // Rompe-caché: el PDF se regenera con el mismo nombre, así forzamos traer la versión nueva
      const pdfUrl = `${pdfUrlBase}${pdfUrlBase.includes('?') ? '&' : '?'}t=${Date.now()}`;

      // Abrir en nueva pestaña
      window.open(pdfUrl, '_blank');
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'PDF no disponible',
        text: 'No se encontró el PDF guardado para este caso.',
        timer: 3000
      });
    }
  };

  if (!isOpen) return null;

  const currentCaso = casoData || caso;
  const meta = currentCaso?.metadata?.correspondencia || {};

  return (
    <>
      <Modal isOpen={isOpen} toggle={safeToggle} size="xl" centered className="modal-correspondencia-premium" backdrop="static" keyboard={false}>
        {/* HEADER COMPACTO CON INFORMACIÓN CLAVE */}
        <ModalHeader toggle={safeToggle} className="bg-white border-bottom py-2">
          <div className="d-flex align-items-center justify-content-between w-100 me-4">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-light rounded-circle p-2">
                <FaFileAlt className="text-success" size={18} />
              </div>
              <div>
                <h6 className="mb-0 fw-bold text-dark">Correspondencia #{currentCaso?.id}</h6>
                <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                  {currentCaso?.name} • Creado: {currentCaso?.created_date}
                </small>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              {/* Botones de Acción Principales (Solo si no está finalizado) */}
              {currentCaso?.estado !== "FINALIZADO" && (
                <div className="d-flex gap-2">
                  {(currentCaso?.estado === "ASIGNADO" || currentCaso?.estado === "SIN_ASIGNAR") && (
                    <Button type="button" color="success" size="sm" onClick={(e) => { e.stopPropagation(); handleAction("ACEPTAR", currentCaso.name, currentCaso.id); }}>
                      <IoCheckmarkDoneSharp className="me-1" /> Aceptar
                    </Button>
                  )}
                  {currentCaso?.estado === "PENDIENTE" && (
                    <Button type="button" color="primary" size="sm" onClick={(e) => { e.stopPropagation(); handleAction("INICIAR", currentCaso.name, currentCaso.id); }}>
                      <IoPlaySharp className="me-1" /> Iniciar
                    </Button>
                  )}
                  {currentCaso?.estado === "EN_PROCESO" && (
                    <>
                      <Button type="button" color="warning" outline size="sm" onClick={(e) => { e.stopPropagation(); handleAction("PAUSAR", currentCaso.name, currentCaso.id); }}>
                        <IoPauseSharp className="me-1" /> Pausar
                      </Button>
                      <Button type="button" color="danger" size="sm" onClick={(e) => { e.stopPropagation(); handleFinalizar(); }}>
                        <IoStopSharp className="me-1" /> Finalizar
                      </Button>
                    </>
                  )}
                  {currentCaso?.estado === "EN_PAUSA" && (
                    <Button type="button" color="primary" outline size="sm" onClick={(e) => { e.stopPropagation(); handleAction("REANUDAR", currentCaso.name, currentCaso.id); }}>
                      <IoPlaySharp className="me-1" /> Reanudar
                    </Button>
                  )}
                </div>
              )}

              <Badge
                color={currentCaso?.estado === 'FINALIZADO' ? 'dark' : currentCaso?.estado === 'EN_PROCESO' ? 'success' : 'warning'}
                className="px-3 py-2"
                style={{ fontSize: '0.8rem' }}
              >
                {currentCaso?.estado}
              </Badge>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="p-0 bg-light">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: '400px' }}>
              <div className="spinner-border text-success" role="status"></div>
            </div>
          ) : (
            <div className="d-flex flex-column flex-lg-row h-100">

              {/* COLUMNA 1: ENTREGAS Y FIRMAS (30%) */}
              <div className="col-lg-4 bg-white border-end p-3 d-flex flex-column gap-3">
                <div className="d-flex align-items-center mb-2 text-success border-bottom pb-2">
                  <FaUser className="me-2" /> <span className="fw-bold text-uppercase small">Datos de quien recibe</span>
                </div>

                <div className="form-group mb-1">
                  <Label className="small mb-1">Nombre y Apellido</Label>
                  <Input
                    bsSize="sm"
                    value={conSello ? '' : (nombreReclamante || '')}
                    onChange={(e) => setNombreReclamante(e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, ''))}
                    placeholder="Nombre de quien recibe"
                    disabled={isFinalizado || conSello}
                    className="fw-bold"
                  />
                </div>

                <Row className="g-2">
                  <Col xs={6}>
                    <Label className="small text-muted mb-1">Cédula / NIT</Label>
                    <Input
                      bsSize="sm"
                      value={conSello ? '' : (cedulaReclamante || '')}
                      onChange={(e) => {
                        let val = e.target.value.replace(/[^0-9\-]/g, '');
                        // Solo un guion permitido, y máximo un dígito después del guion (dígito verificador NIT)
                        const partes = val.split('-');
                        if (partes.length > 2) val = partes[0] + '-' + partes.slice(1).join('');
                        if (partes.length === 2) val = partes[0] + '-' + partes[1].slice(0, 1);
                        setCedulaReclamante(val);
                      }}
                      disabled={isFinalizado || conSello}
                    />
                  </Col>
                  <Col xs={6}>
                    <Label className="small text-muted mb-1">Teléfono</Label>
                    <Input
                      bsSize="sm"
                      value={conSello ? '' : (telefonoReclamante || '')}
                      onChange={(e) => setTelefonoReclamante(e.target.value)}
                      disabled={isFinalizado || conSello}
                    />
                  </Col>
                </Row>

                <div className="bg-light p-2 rounded border mt-1">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">Dirección de Entrega</small>
                    <FaMapMarkerAlt className="text-danger" size={12} />
                  </div>
                  <div className="fw-bold text-dark small text-truncate" title={meta.direccion_reclamante}>
                    {meta.direccion_reclamante || 'Sin dirección'}
                  </div>
                  <small className="text-secondary d-block" style={{ fontSize: '0.75rem' }}>
                    {meta.localidad || 'Localidad no especificada'}
                  </small>
                </div>

                <div className="mt-auto">
                  <Button type="button"
                    color={tempRepresentanteFirma ? "success" : "light"}
                    block
                    outline={!tempRepresentanteFirma}
                    className={`d-flex align-items-center justify-content-center w-100 border ${!tempRepresentanteFirma ? 'text-muted' : ''}`}
                    onClick={() => {
                      if (!isEditable && !isFinalizado) { handleBlockedAction(); return; }
                      setShowRepresentanteSignature(true);
                    }}
                    disabled={isFinalizado || conSello}
                    style={{ borderStyle: 'dashed' }}
                  >
                    {tempRepresentanteFirma ? <IoCheckmarkDoneSharp className="me-2" /> : <FaPenNib className="me-2" />}
                    {tempRepresentanteFirma ? "Firma Capturada" : "Capturar Firma Recibe"}
                  </Button>

                  <div className="mt-3 p-2 border rounded bg-light">
                    <div className="form-check mb-2">
                      <Input
                        type="checkbox"
                        id="con_sello_chk"
                        checked={conSello}
                        onChange={(e) => {
                          if (!isEditable && !isFinalizado) { handleBlockedAction(); return; }
                          setConSello(e.target.checked);
                        }}
                        disabled={isFinalizado || !isEditable}
                      />
                      <Label htmlFor="con_sello_chk" className="small fw-bold mb-0 text-dark">Entregar con sello / evidencia</Label>
                    </div>

                    <div className="d-flex flex-column gap-2">

                      {/* --- SELLO --- */}
                      {conSello && (
                        <div className="border rounded p-2 bg-white">
                          <small className="fw-bold text-dark d-block mb-1">📋 Imagen del Sello</small>
                          {previewSello && (
                            <div className="position-relative mb-2" style={{ textAlign: 'center' }}>
                              <img
                                src={previewSello}
                                alt="Sello"
                                className="img-thumbnail bg-white"
                                style={{ height: '90px', objectFit: 'contain', maxWidth: '100%' }}
                              />
                              {!isFinalizado && isEditable && (
                                <Button type="button"
                                  color="danger"
                                  size="xs"
                                  className="position-absolute top-0 end-0 p-0 m-1 lh-1 fw-bold rounded-circle border border-white text-white d-flex align-items-center justify-content-center shadow"
                                  style={{ width: '20px', height: '20px', fontSize: '12px', zIndex: 10 }}
                                  onClick={() => { setPreviewSello(null); setArchivoSello(null); }}
                                >
                                  ✕
                                </Button>
                              )}
                            </div>
                          )}
                          <Button type="button"
                            color="success"
                            size="sm"
                            outline
                            block
                            onClick={triggerCameraSello}
                            disabled={isFinalizado || !isEditable}
                          >
                            📷 {previewSello ? 'Reemplazar Sello' : 'Subir Sello'}
                          </Button>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            ref={fileInputSelloRef}
                            style={{ display: 'none' }}
                            onChange={handleCaptureSello}
                          />
                        </div>
                      )}

                      {/* --- CNP --- */}
                      <div className="border rounded p-2 bg-white">
                        <small className="fw-bold text-dark d-block mb-1">📋 CNP</small>
                        {previewCNP && (
                          <div className="position-relative mb-2" style={{ textAlign: 'center' }}>
                            <img
                              src={previewCNP}
                              alt="CNP"
                              className="img-thumbnail bg-white"
                              style={{ height: '90px', objectFit: 'contain', maxWidth: '100%' }}
                            />
                            {!isFinalizado && isEditable && (
                              <Button type="button"
                                color="danger"
                                size="xs"
                                className="position-absolute top-0 end-0 p-0 m-1 lh-1 fw-bold rounded-circle border border-white text-white d-flex align-items-center justify-content-center shadow"
                                style={{ width: '20px', height: '20px', fontSize: '12px', zIndex: 10 }}
                                onClick={() => { setPreviewCNP(null); setArchivoCNP(null); }}
                              >
                                ✕
                              </Button>
                            )}
                          </div>
                        )}
                        <Button type="button"
                          color="warning"
                          size="sm"
                          block
                          onClick={triggerCameraCNP}
                          disabled={isFinalizado || !isEditable}
                        >
                          📷 {previewCNP ? 'Reemplazar CNP' : 'Subir CNP'}
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          ref={fileInputCNPRef}
                          style={{ display: 'none' }}
                          onChange={handleCaptureCNP}
                        />
                      </div>

                      {/* --- FOTOS DE EVIDENCIA --- */}
                      <div className="border rounded p-2 bg-white">
                        <small className="fw-bold text-dark d-block mb-1">📸 Fotos de Evidencia</small>
                        {previewsFotos.length > 0 && (
                          <div className="d-flex flex-wrap gap-2 mb-2 justify-content-center">
                            {previewsFotos.map((preview, idx) => (
                              <div key={idx} className="position-relative" style={{ width: '45%' }}>
                                <img
                                  src={preview}
                                  alt={`Foto ${idx + 1}`}
                                  className="img-thumbnail w-100 bg-white"
                                  style={{ height: '80px', objectFit: 'cover' }}
                                />
                                {!isFinalizado && isEditable && (
                                  <Button type="button"
                                    color="danger"
                                    size="xs"
                                    className="position-absolute top-0 end-0 p-0 m-1 lh-1 fw-bold rounded-circle border border-white text-white d-flex align-items-center justify-content-center shadow"
                                    style={{ width: '20px', height: '20px', fontSize: '12px', zIndex: 10 }}
                                    onClick={() => {
                                      setPreviewsFotos(prev => prev.filter((_, i) => i !== idx));
                                      setArchivosFotos(prev => prev.filter((_, i) => i !== idx));
                                    }}
                                  >
                                    ✕
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <Button type="button"
                          color="primary"
                          size="sm"
                          outline
                          block
                          onClick={triggerCameraFotos}
                          disabled={isFinalizado || !isEditable}
                        >
                          📷 {previewsFotos.length > 0 ? 'Añadir Foto' : 'Subir Fotos'}
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          multiple
                          ref={fileInputFotosRef}
                          style={{ display: 'none' }}
                          onChange={handleCapturaFoto}
                        />
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              {/* COLUMNA 2: DETALLES DEL DOCUMENTO (40%) */}
              <div className="col-lg-5 bg-light p-3 border-end">
                <div className="d-flex align-items-center mb-3 text-primary border-bottom pb-2">
                  <FaFileAlt className="me-2" /> <span className="fw-bold text-uppercase small">Detalles del Documento</span>
                </div>

                <Row className="g-3">
                  {/* Fila 1 */}
                  <Col xs={6}>
                    <div className="p-2 bg-white border rounded h-100 shadow-sm">
                      <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>MOTIVO</small>
                      <div className="fw-bold text-dark small">{meta.motivo || 'N/A'}</div>
                      {meta.codigo_motivo && <Badge color="light" className="text-dark border mt-1">{meta.codigo_motivo}</Badge>}
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="p-2 bg-white border rounded h-100 shadow-sm">
                      <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>CONCEPTO</small>
                      <div className="fw-bold text-dark small text-truncate" title={meta.concepto}>{meta.concepto || 'N/A'}</div>
                    </div>
                  </Col>

                  {/* Fila 2 - Nuevos Campos */}
                  <Col xs={6}>
                    <div className="p-2 bg-white border rounded h-100 shadow-sm">
                      <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>CUENTA CONTRATO</small>
                      <div className="fw-bold text-dark small">{meta.cuenta_contrato || 'N/A'}</div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="p-2 bg-white border rounded h-100 shadow-sm">
                      <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>No. PQRS</small>
                      <div className="fw-bold text-dark small">{meta.no_pqrs || 'N/A'}</div>
                    </div>
                  </Col>

                  {/* Fila 3 */}
                  <Col xs={6}>
                    <div className="p-2 bg-white border rounded h-100 shadow-sm">
                      <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>PLANILLA</small>
                      <div className="text-dark small">{meta.planilla || 'N/A'}</div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="p-2 bg-white border rounded h-100 shadow-sm">
                      <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>No. RESOLUCIÓN</small>
                      <div className="text-dark small">{meta.no_resolucion || 'N/A'}</div>
                    </div>
                  </Col>

                  {/* Fechas */}
                  <Col xs={12}>
                    <div className="d-flex justify-content-between bg-white border rounded p-2 shadow-sm">
                      <div className="text-center w-50 border-end">
                        <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>FECHA DE EMISIÓN</small>
                        <span className="text-dark small fw-bold">{meta.fecha_original || 'N/A'}</span>
                      </div>
                      <div className="text-center w-50">
                        <small className="d-block text-muted" style={{ fontSize: '0.7rem' }}>FECHA LÍMITE</small>
                        <span className="text-danger small fw-bold">{meta.fecha_limite || 'N/A'}</span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* COLUMNA 3: CAUSAS Y ACCIONES (30%) */}
              <div className="col-lg-3 bg-white p-3 d-flex flex-column">
                <div className="d-flex align-items-center mb-2 text-warning border-bottom pb-2">
                  <FaInfoCircle className="me-2" /> <span className="fw-bold text-uppercase small text-dark">Causas Devolución</span>
                </div>

                <div className="flex-grow-1 overflow-auto pe-1">
                  {[
                    { id: 'chk_vacio', label: 'Predio Vacío', state: coVacio, setter: setCoVacio },
                    { id: 'chk_no_recibe', label: 'No hay quien reciba', state: coNoHayQuienReciba, setter: setCoNoHayQuienReciba },
                    { id: 'chk_rehusado', label: 'Rehusado', state: coRehusado, setter: setCoRehusado },
                    { id: 'chk_dir_errada', label: 'Dirección Errada', state: coDireccionErrada, setter: setCoDireccionErrada },
                    { id: 'chk_alto_riesgo', label: 'Alto Riesgo', state: coAltoRiesgo, setter: setCoAltoRiesgo },
                    { id: 'chk_error', label: 'Error en Datos', state: coErrorEnLosDatos, setter: setCoErrorEnLosDatos },
                    { id: 'chk_correo', label: 'Correo Certificado', state: coCorreoCertificado, setter: setCoCorreoCertificado },
                  ].map((item) => (
                    <div key={item.id} className="form-check form-switch mb-2">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id={item.id}
                        checked={item.state}
                        onChange={(e) => {
                          if (!isEditable && !isFinalizado) { handleBlockedAction(); return; }
                          item.setter(e.target.checked);
                        }}
                        disabled={isFinalizado || !isEditable}
                      />
                      <Label className="form-check-label small text-dark" htmlFor={item.id} style={{ cursor: 'pointer' }}>
                        {item.label}
                      </Label>
                    </div>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-top">
                  {!isFinalizado ? (
                    <div className="d-grid gap-2">
                      <Button type="button"
                        color="dark"
                        outline
                        size="sm"
                        onClick={handleViewTicket}
                        disabled={isGeneratingTicket}
                      >
                        {isGeneratingTicket ? <span className="spinner-border spinner-border-sm" /> : <FaEye className="me-2" />}
                        Vista Previa PDF
                      </Button>
                      <Button type="button"
                        color="success"
                        size="sm"
                        onClick={handleUpdateCorrespondencia}
                        disabled={loading || !isEditable}
                      >
                        Guardar Cambios
                      </Button>
                    </div>
                  ) : (
                    <Button type="button"
                      color="dark"
                      block
                      onClick={handleViewSavedPDF}
                      className="w-100"
                    >
                      <FaEye className="me-2" /> Ver PDF Guardado
                    </Button>
                  )}
                  {/* Removed generic Descargar PDF button as requested */}
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal >

      {/* SweetAlert para confirmación de acciones */}
      {
        actionAlert && (
          <SweetAlert
            title={`¿${action} Caso?`}
            warning
            showConfirm={true}
            confirmBtnText="Sí, Confirmar"
            showCancel={true}
            cancelBtnBsStyle="light"
            cancelBtnText="Cancelar"
            onCancel={() => setActionAlert(false)}
            onConfirm={() => actionSuccess()}
            dependencies={[action, ticket]}
          >
            <div className="text-muted small mt-2">
              Se registrará su ubicación y cambio de estado.
            </div>
            <Label className="mt-3 fw-bold small">Observaciones (Opcional)</Label>
            <Input
              type="textarea"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              className="form-control-sm"
              placeholder="Ingrese detalles adicionales..."
            />
          </SweetAlert>
        )
      }

      {/* SweetAlert de éxito */}
      {
        showAlertSuccess && (<SweetAlert title="¡Completado!" success showConfirm={false} timeout={2000} onConfirm={() => setShowAlertSuccess(false)} />)
      }

      {/* SweetAlert de error */}
      {
        errorAlert && (<SweetAlert title="¡Error!" danger showConfirm={false} timeout={2000} onConfirm={() => setErrorAlert(false)} />)
      }

      {/* Modal de Firma Representante */}
      <SignatureModal
        isOpen={showRepresentanteSignature}
        onClose={() => setShowRepresentanteSignature(false)}
        onSave={handleSaveRepresentanteSignature}
        title="Firma quien Recibe"
      />

      {/* Modal de Firma Funcionario */}
      <SignatureModal
        isOpen={showCiudadLimpiaSignature}
        onClose={() => setShowCiudadLimpiaSignature(false)}
        onSave={handleSaveCiudadLimpiaSignature}
        title="Firma Funcionario"
      />

      <Modal isOpen={isTicketPreviewOpen} toggle={() => setIsTicketPreviewOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setIsTicketPreviewOpen(false)}>Vista Previa del Ticket</ModalHeader>
        <ModalBody style={{ height: '80vh', padding: 0, overflow: 'hidden' }}>
          {ticketPreviewData ? (
            <PDFViewer style={{ width: '100%', height: '100%', border: 'none' }} showToolbar={true}>
              <TiqueteCorrespondenciaPDF data={ticketPreviewData} />
            </PDFViewer>
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100">Cargando...</div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default CorrespondenciaModal;
